import { createRoute, Outlet, redirect } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { appState } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@apunte-digital/shared";
import { queryClient } from "@/lib/query-client";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { readTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Apunte's authentication route-layout.
 *
 * It is deliberately thin: the visual COMPOSITION (background, safe-areas,
 * brand masthead + notes card) now lives in `ApunteScene`
 * (`components/auth`), Apunte's own shell (index-card with a bound edge + margin
 * rule, different from the shared frame that made all the apps look like
 * clones). Each screen passes its `headline`/`eyebrow` and pours its form as
 * `children`. This route only contributes the `beforeLoad` (redirects if there
 * is already a session) and mounts the `<Outlet/>`.
 */
function AuthLayoutRoute() {
  appState.markAsReady();
  return <Outlet />;
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    const redirectTarget = (search as { redirect?: string }).redirect;
    const tabUserId = readTabAccountId();
    if (tabUserId !== null) {
      const view = resolveActiveAccountView(
        readAccountsStateSync(),
        tabUserId,
        sessionActor.getSnapshot().context.session,
      );
      // Guest tab → show /login (do NOT redirect). Real account → bounce home.
      if (view.isAuthenticated) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: redirectTarget ?? "/" });
      }
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` CANNOT be unbounded. A STALLED
    // `get-session` (held, neither 401 nor 5xx: both resolve fast) left this
    // `beforeLoad` unresolved, the router without mounting the route and
    // `/login` BLANK — `innerText` empty at 3/6/10/15/20 s, measured in this
    // repo against the dev server and the production bundle, with `/` painting
    // in the same run as control. The sibling `_app` guard already ran with a
    // ceiling; this one was left without it because the ceiling was a local
    // literal, and that is exactly why I1 closed green measuring only the root.
    //
    // The request is NOT cancelled: it stays alive and syncs machine + cache
    // when it arrives. If there turned out to be a session, the `BootManager`
    // takes the user out of the login screen; only a flicker is paid, never an
    // empty screen.
    const fetchSession = async () =>
      api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

    const syncReply = (reply: FetchResult) => {
      const { data: replyData, error: replyError } = reply;
      // Synchronize the machine and the cache immediately.
      // This prevents the BootManager from seeing a "pending" or stale session
      // during the route transition, which causes redirection loops.
      sessionActor.send({
        type: "SERVER_REPLY",
        session: isValidSession(replyData) ? cloneSession(replyData) : null,
        error: replyError ?? null,
      });
      queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
      return reply;
    };

    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout or network error: the login IS PAINTED. The late response is
      // already hooked above (`syncReply`) and the BootManager redirects if it
      // carries a session.
      return;
    }

    // Redirect is thrown OUTSIDE the race so it propagates to TanStack Router.
    // Decide from the session the machine ACCEPTED, not from the raw reply: a
    // reply for a session that LOGOUT revoked is refused by the machine, and
    // redirecting on it would bounce a signed-out guest back into the app.
    // 401/403, network error, or null session — not authenticated, show login page.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
  },
  component: AuthLayoutRoute,
});
